import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import * as aiBillingApi from '../../api/aiBilling';
import { useAuthStore } from '../../store/authStore';
import UpgradePlanModal from '../billing/UpgradePlanModal';

const planColors = {
  none: 'text-gray-500 bg-gray-100',
  starter: 'text-blue-600 bg-blue-50',
  pro: 'text-purple-600 bg-purple-50',
  unlimited: 'text-amber-600 bg-amber-50',
};

export default function AIUsageWidget() {
  const { user } = useAuthStore();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const loadUsage = () => {
    aiBillingApi.getUsage()
      .then(data => setSub(data.subscription))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUsage(); }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
        <div className="h-4 bg-gray-100 rounded w-24 mb-3" />
        <div className="h-8 bg-gray-100 rounded w-16" />
      </div>
    );
  }

  if (!sub) return null;

  const isUnlimited = sub.queries_limit === null;
  const isNone = sub.plan_name === 'none';
  const usagePercent = isUnlimited ? 0 : isNone ? 0 : Math.min(100, Math.round((sub.queries_used / sub.queries_limit) * 100));

  // Color coding for progress bar
  let barColor = 'bg-green-500';
  if (usagePercent >= 80) barColor = 'bg-yellow-500';
  if (usagePercent >= 95) barColor = 'bg-red-500';

  const daysLeft = Math.max(0, Math.ceil((new Date(sub.billing_cycle_end) - new Date()) / (1000 * 60 * 60 * 24)));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm text-gray-500">AI Assistant</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${planColors[sub.plan_name] || 'text-gray-500 bg-gray-100'}`}>
              {sub.plan_display_name}
            </span>
          </div>
        </div>
        <div className="text-right">
          {isNone ? (
            <p className="text-lg font-bold text-gray-400">—</p>
          ) : isUnlimited ? (
            <p className="text-lg font-bold text-gray-900">{sub.queries_used}</p>
          ) : (
            <p className="text-lg font-bold text-gray-900">{sub.queries_used}<span className="text-sm font-normal text-gray-400">/{sub.queries_limit}</span></p>
          )}
          {!isNone && <p className="text-xs text-gray-400">queries this cycle</p>}
        </div>
      </div>

      {/* Progress bar */}
      {!isNone && !isUnlimited && (
        <div className="mb-2">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${usagePercent}%` }} />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-gray-400">{usagePercent}% used</span>
            <span className="text-[10px] text-gray-400">{daysLeft} day{daysLeft !== 1 ? 's' : ''} left</span>
          </div>
        </div>
      )}

      {isUnlimited && (
        <p className="text-[10px] text-gray-400">{daysLeft} day{daysLeft !== 1 ? 's' : ''} left in cycle</p>
      )}

      {isNone && (
        <p className="text-xs text-gray-400 mt-1">AI features are not enabled</p>
      )}

      {/* Deep analysis usage */}
      {!isNone && sub.deep_analyses_limit !== null && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Deep Analysis</span>
            <span>{sub.deep_analyses_used}/{sub.deep_analyses_limit}</span>
          </div>
        </div>
      )}

      {/* Upgrade button (admin only) */}
      {user?.is_admin && (
        <button
          onClick={() => setShowUpgrade(true)}
          className="mt-3 w-full text-xs font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 py-1.5 rounded-lg transition-colors"
        >
          {isNone ? 'Enable AI' : 'Manage Plan'}
        </button>
      )}

      <UpgradePlanModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        currentPlan={sub.plan_name}
        onUpgraded={loadUsage}
      />
    </div>
  );
}
